import React, { createContext, useContext, useMemo, useState } from 'react'

// Shown only while that tour step is up, and only where the real thing is missing.
export type TourExample = 'feed' | 'symptom-survey' | 'care-team-member' | 'event-log'

type Value = {
  example: TourExample | null
  setExample: (example: TourExample | null) => void
  running: boolean
  setRunning: (running: boolean) => void
}

const TourExampleContext = createContext<Value | null>(null)

const noop = (): void => undefined

export const TourExampleProvider = ({ children }: { children: React.ReactNode }) => {
  const [example, setExample] = useState<TourExample | null>(null)
  const [running, setRunning] = useState(false)
  const value = useMemo(() => ({ example, setExample, running, setRunning }), [example, running])

  return <TourExampleContext.Provider value={value}>{children}</TourExampleContext.Provider>
}

export const useTourExample = (): TourExample | null => useContext(TourExampleContext)?.example ?? null

export const useSetTourExample = (): Value['setExample'] => useContext(TourExampleContext)?.setExample ?? noop

// True while the walkthrough is up, so a row's actions can show without running.
export const useIsTourRunning = (): boolean => useContext(TourExampleContext)?.running ?? false

export const useSetTourRunning = (): Value['setRunning'] => useContext(TourExampleContext)?.setRunning ?? noop

// Rows drawn from example data: their actions do nothing.
const ExampleRowsContext = createContext(false)

export const ExampleRowsProvider = ExampleRowsContext.Provider

export const useIsExampleRow = (): boolean => useContext(ExampleRowsContext)
